import {
  Home,
  Heart,
  Calendar,
  Image as ImageIcon,
  Quote,
  Disc,
  Pause,
  Music,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useRef, useEffect } from "react";
import bgm from "@/assets/audio/bgm.mp3";

interface FloatingNavProps {
  isPlaying: boolean;
  togglePlay: () => void;
}

export function FloatingNav({ isPlaying, togglePlay }: FloatingNavProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch((error) => {
          console.log("Autoplay blocked:", error);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { id: "hero", icon: Home, label: "Home" },
    { id: "quotes", icon: Quote, label: "Quotes" },
    { id: "couple", icon: Heart, label: "Couple" },
    { id: "event", icon: Calendar, label: "Event" },
    { id: "gallery", icon: ImageIcon, label: "Gallery" },
  ];

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center justify-center w-full pointer-events-none">
      <audio ref={audioRef} src={bgm} loop />

      <motion.div
        className="flex items-center gap-2 p-1.5 bg-white/80 backdrop-blur-md border border-white/40 rounded-full shadow-lg pointer-events-auto"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring", stiffness: 260, damping: 20 }}
      >
        {/* Navigation Items */}
        <div className="flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="p-3 rounded-full text-stone-600 hover:text-stone-900 hover:bg-white/50 transition-all relative group"
              aria-label={item.label}
            >
              <item.icon className="w-5 h-5" />
              <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                {item.label}
              </span>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-stone-300 mx-1"></div>

        {/* Music Toggle Button */}
        <button
          onClick={togglePlay}
          className={`p-2 rounded-full transition-all relative group flex items-center justify-center ${isPlaying ? "bg-stone-100 text-stone-900" : "text-stone-400 hover:text-stone-600"}`}
          aria-label={isPlaying ? "Pause Music" : "Play Music"}
        >
          <motion.div
            animate={{ rotate: isPlaying ? 360 : 0 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="relative"
          >
            <Disc className="w-6 h-6" />
            <div className="absolute inset-0 flex items-center justify-center">
              {isPlaying ? (
                <Pause className="w-2.5 h-2.5 fill-current" />
              ) : (
                <Music className="w-2.5 h-2.5 fill-current" />
              )}
            </div>
          </motion.div>
        </button>
      </motion.div>
    </div>
  );
}
